import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {
  CdkDragDrop,
  CdkDragEnd,
  CdkDragMove,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import {
  OverlayService,
  State,
  AppSvgIconComponent,
  SignalButton,
  SignalCheckbox,
  SignalDropdown,
  SignalRadioGroup,
  SignalSwitch,
  SignalTextField,
  type SignalRadioOption,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type {
  AiGenerateApiResponse,
  AiGenerateRequest,
  ContactGroupResponse,
  ContactResponse,
  CreateContactsApiResponse,
  GetFormApiResponse,
  ListContactGroupsApiResponse,
  ListContactsApiResponse,
  PublishFormApiResponse,
  PublishFormRequest,
  SaveFormDefinitionApiResponse,
} from '@core/api/workspace/models';
import { AiGenerateRequestMode } from '@core/api/workspace/models';
import { TokenStorageService } from '@core/services/token-storage.service';
import {
  resolveAvatarColorClass,
  resolveInitials,
} from '@core/utils/initial-avatar.util';
import { AccountMenuButtonComponent } from '../../../layout/components/account-menu-button/account-menu-button';
import { LayoutService } from '../../../layout/layout.service';
import { FieldConfigPanelComponent } from '../../components/field-config-panel/field-config-panel';
import { FormPagePagerComponent } from '../../components/form-page-pager/form-page-pager';
import {
  FormPagesOverlayComponent,
  type FormPagesOverlayResult,
} from '../../components/form-pages-overlay/form-pages-overlay';
import { FormPageStepConfigPanelComponent } from '../../components/form-page-step-config-panel/form-page-step-config-panel';
import { PageConfigPanelComponent } from '../../components/page-config-panel/page-config-panel';
import { WorkflowActionConfigPanelComponent } from '../../components/workflow-action-config-panel/workflow-action-config-panel';
import {
  WorkflowActionConfigOverlayComponent,
  type WorkflowActionConfigOverlayResult,
} from '../../components/workflow-action-config-overlay/workflow-action-config-overlay';
import { TooltipDirective } from '@shared/components/tooltip/tooltip.directive';
import {
  CreateContactOverlayComponent,
  type CreateContactOverlayResult,
} from '../../../people/create-contact-overlay/create-contact-overlay';
import {
  ADVANCED_FIELD_CATALOG,
  BASIC_FIELD_CATALOG,
  FIELD_CATALOG,
  FieldCatalogItem,
  INTERMEDIATE_FIELD_CATALOG,
  LAYOUT_FIELD_CATALOG,
  catalogIconSrc,
  matchesFieldCatalogSearch,
} from '../../utils/form-field-catalog';
import {
  cloneFieldWithNewId,
  createEmptyBuiltForm,
  createFieldFromCatalog,
} from '../../utils/create-field-from-catalog';
import type { BuiltForm, ChatMessage, FormField, FormPage } from '../../models/built-form';
import { FORM_SUGGESTIONS } from '../../utils/mock-ai-form-generator';
import { toAiCatalogPayload } from '../../utils/to-ai-catalog-payload';
import {
  mapActivePageToAiDto,
  mapBuiltFormDtoToBuiltForm,
  mapBuiltFormToDto,
} from '../../utils/map-built-form-dto';
import {
  allFormFields,
  applyPageSequence,
  createDefaultPage,
  DEFAULT_NEXT_LABEL,
  DEFAULT_PAGE_ID,
  DEFAULT_PRIMARY_LABEL,
  findPage,
  nextPageId,
  reindexPageOrders,
  sortedPages,
  updatePageFields,
} from '../../utils/form-pages';
import {
  WORKFLOW_ACTION_CATALOG,
  WorkflowAction,
  WorkflowActionCatalogItem,
  WorkflowBranch,
  WorkflowPosition,
  branchActions,
  collectDescendantIds,
  createActionFromCatalog,
  rootActions,
} from '../../utils/workflow-action-catalog';
import {
  FORM_SUBMISSION_DRAFT_RETENTION_OPTIONS,
  FormPublishConfig,
  FormVisibility,
  PublishTiming,
  SUBMISSION_REFERENCE_DIGIT_LENGTH_OPTIONS,
  SUBMISSION_REFERENCE_TOKEN_CHIPS,
  SubmissionReferenceMode,
  buildPublishedFormAbsoluteUrl,
  createDefaultFormPublishConfig,
  defaultCustomSubmissionReferencePattern,
  draftRetentionHoursFromConfig,
  draftRetentionOptionFromHours,
  getSubmissionReferencePublishError,
  hasValidSubmissionReferenceSeqToken,
  isValidFormSlug,
  normalizeFormSlug,
  resolveSubmissionReferencePreview,
  submissionReferenceDigitLengthFromValue,
  submissionReferenceModeFromValue,
} from '../../utils/form-publish-config';
import { CreateFormAiHandoffService } from '../../services/create-form-ai-handoff.service';
import { FormPreviewHandoffService } from '../../services/form-preview-handoff.service';
import { FormFieldPreviewControlComponent } from '../../components/form-field-preview-control/form-field-preview-control.component';
import { previewControlClass } from '../../utils/form-preview-styles';
import {
  themeAccentHex,
  themeCssVars,
} from '../../utils/form-theme-catalog';
import { enrichBuiltFormVariableDefaults } from '../../utils/resolve-linked-variable';
import { enrichBuiltFormCollectionRefs } from '../../utils/resolve-collection-dropdown-items';

export type BuilderMode = 'ai' | 'manual';
export type BuilderStep = 'form' | 'workflow' | 'config';
export type FormSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface BuilderStepMeta {
  id: BuilderStep;
  label: string;
}

/** Screenshot-style compare tabs under the numbered stepper (Integrations is visual-only). */
export interface BuilderCompareTab {
  id: BuilderStep | 'integrations';
  label: string;
  iconSrc: string;
  disabled?: boolean;
}

interface WorkflowEdge {
  id: string;
  d: string;
}

interface WorkflowPickerTarget {
  parentId: string | null;
  branch: WorkflowBranch | null;
}

interface BranchBubblePair {
  then: WorkflowPosition;
  else: WorkflowPosition;
}

interface WorkflowBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

const ALL_BUILDER_STEPS: BuilderStepMeta[] = [
  { id: 'form', label: 'Form' },
  { id: 'workflow', label: 'Workflow' },
  { id: 'config', label: 'Publish' },
];

const BUILDER_COMPARE_TABS: BuilderCompareTab[] = [
  {
    id: 'form',
    label: 'Form builder',
    iconSrc: 'assets/icons/square-edit-outline.svg',
  },
  {
    id: 'workflow',
    label: 'Workflow',
    iconSrc: 'assets/icons/lightning-bolt-outline.svg',
  },
  {
    id: 'integrations',
    label: 'Integrations',
    iconSrc: 'assets/icons/widgets-outline.svg',
    disabled: true,
  },
  {
    id: 'config',
    label: 'Publish',
    iconSrc: 'assets/icons/share-variant-outline.svg',
  },
];

/** Flip to `true` to restore the Workflow step in the editor. */
const WORKFLOW_STEP_ENABLED = true;

/** Flip to `true` to show the numbered Form / Workflow / Publish stepper in the header. */
const NUMBERED_STEPPER_ENABLED = false;

const BUILDER_STEP_ORDER: BuilderStep[] = WORKFLOW_STEP_ENABLED
  ? ['form', 'workflow', 'config']
  : ['form', 'config'];

const WORKFLOW_NODE_WIDTH = 320;
const WORKFLOW_ACTION_HEIGHT = 56;
const WORKFLOW_BRANCH_HEIGHT = 120;
const WORKFLOW_BUBBLE_SIZE = 40;
const WORKFLOW_NODE_GAP = 64;
const WORKFLOW_BRANCH_OFFSET_X = 180;
/** Bottom-center of Condition row relative to if_else card top. */
const WORKFLOW_IF_THEN_PORT_Y = 88;
/** Bottom-center of Else row relative to if_else card top. */
const WORKFLOW_IF_ELSE_PORT_Y = 112;
const WORKFLOW_TRIGGER_POSITION: WorkflowPosition = { x: 180, y: 40 };
const WORKFLOW_ZOOM_MIN = 0.25;
const WORKFLOW_ZOOM_MAX = 2;
const WORKFLOW_ZOOM_STEP = 0.1;
const WORKFLOW_PICKER_WIDTH = 256;
const WORKFLOW_PICKER_MAX_HEIGHT = 280;
const AUDIENCE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function toIsoFromDatetimeLocal(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
}

function deriveContactFirstName(email: string): string {
  const local = email.split('@')[0] ?? '';
  const token = local.split(/[._+-]+/).find((part) => part.length > 0) ?? '';
  if (!token) {
    return 'Contact';
  }
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

@Component({
  selector: 'app-ai-form-builder-page',
  standalone: true,
  imports: [
    FormsModule,
    DragDropModule,
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    AccountMenuButtonComponent,
    AppSvgIconComponent,
    FieldConfigPanelComponent,
    FormPagePagerComponent,
    FormPageStepConfigPanelComponent,
    PageConfigPanelComponent,
    WorkflowActionConfigPanelComponent,
    FormFieldPreviewControlComponent,
    TooltipDirective,
    SignalButton,
    SignalCheckbox,
    SignalDropdown,
    SignalRadioGroup,
    SignalSwitch,
    SignalTextField,
  ],
  templateUrl: './ai-form-builder-page.html',
  styleUrl: './ai-form-builder-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'absolute inset-0 flex flex-col overflow-hidden bg-white',
  },
})
export class AiFormBuilderPage extends BaseComponent implements OnInit, OnDestroy {
  private readonly layoutService = inject(LayoutService);
  private readonly overlayService = inject(OverlayService);
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly formPreviewHandoff = inject(FormPreviewHandoffService);
  private readonly createFormAiHandoff = inject(CreateFormAiHandoffService);
  private readonly chatScroll = viewChild<ElementRef<HTMLElement>>('chatScroll');
  private readonly promptInput = viewChild<ElementRef<HTMLTextAreaElement>>('promptInput');
  private readonly formPreviewScroll = viewChild<ElementRef<HTMLElement>>('formPreviewScroll');
  private readonly workflowViewport = viewChild<ElementRef<HTMLElement>>('workflowViewport');
  private readonly workflowStage = viewChild<ElementRef<HTMLElement>>('workflowStage');
  private readonly workflowSpacer = viewChild<ElementRef<HTMLElement>>('workflowSpacer');
  private readonly submissionReferencePatternField =
    viewChild<SignalTextField>('submissionReferencePatternField');
  private previousSidebarExpanded = true;

  /** Workspace form slug from `/forms/builder/:slug`. */
  readonly formSlug = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('slug') ?? '')),
    { initialValue: this.route.snapshot.paramMap.get('slug') ?? '' },
  );

  readonly generateState = new State<AiGenerateApiResponse>();
  readonly loadFormState = new State<GetFormApiResponse>();
  readonly saveFormState = new State<SaveFormDefinitionApiResponse>();
  readonly audienceContactsState = new State<ListContactsApiResponse>();
  readonly audienceGroupsState = new State<ListContactGroupsApiResponse>();
  readonly audienceInviteState = new State<CreateContactsApiResponse>();
  readonly audienceContacts = signal<ContactResponse[]>([]);
  readonly audienceGroups = signal<ContactGroupResponse[]>([]);
  readonly audienceTab = signal<'groups' | 'contacts'>('groups');
  readonly audienceSearch = signal('');
  readonly audienceInviteDraft = signal('');
  readonly audienceInviting = signal(false);
  private audienceLoaded = false;

  readonly visibilityCardOptions: readonly {
    value: FormVisibility;
    title: string;
    hint: string;
    iconSrc: string;
  }[] = [
    {
      value: 'public',
      title: 'Anyone with the link',
      hint: 'Share the link with anyone. No sign in required.',
      iconSrc: 'assets/icons/web.svg',
    },
    {
      value: 'workspace',
      title: 'Specific people',
      hint: 'Only selected contacts and groups can open and submit.',
      iconSrc: 'assets/icons/account-multiple-outline.svg',
    },
    {
      value: 'password_protected',
      title: 'Password protected',
      hint: 'Anyone with the link must enter a password.',
      iconSrc: 'assets/icons/lock-outline.svg',
    },
  ];

  readonly publishTimingOptions: readonly SignalRadioOption<PublishTiming>[] = [
    {
      value: 'immediate',
      label: 'Publish immediately',
      hint: 'Go live as soon as you publish.',
    },
    {
      value: 'scheduled',
      label: 'Schedule publish',
      hint: 'Choose a date and time to open the form.',
    },
  ];

  readonly draftRetentionOptions = [
    ...FORM_SUBMISSION_DRAFT_RETENTION_OPTIONS,
  ];

  readonly submissionReferenceDigitLengthOptions = [
    ...SUBMISSION_REFERENCE_DIGIT_LENGTH_OPTIONS,
  ];

  readonly submissionReferenceTokenChips = [
    ...SUBMISSION_REFERENCE_TOKEN_CHIPS,
  ];

  readonly submissionReferenceModeOptions: ReadonlyArray<{
    value: SubmissionReferenceMode;
    label: string;
  }> = [
    { value: 'standard', label: 'Standard' },
    { value: 'custom', label: 'Custom' },
  ];

  readonly publishFormState = new State<PublishFormApiResponse>();
  readonly saveStatus = signal<FormSaveStatus>('idle');
  readonly publishing = signal(false);
  readonly hasAccessPassword = signal(false);
  readonly formStatus = signal<'draft' | 'published' | 'unpublished' | 'finished'>('draft');
  readonly suggestions = FORM_SUGGESTIONS;
  readonly catalogIconSrc = catalogIconSrc;
  readonly componentCount = FIELD_CATALOG.length;
  readonly aiLayoutComponents = LAYOUT_FIELD_CATALOG;
  readonly aiBasicComponents = BASIC_FIELD_CATALOG;
  readonly aiIntermediateComponents = INTERMEDIATE_FIELD_CATALOG;
  readonly aiAdvancedComponents = ADVANCED_FIELD_CATALOG;
  readonly workflowActionCatalog = WORKFLOW_ACTION_CATALOG;
  readonly dragPlaceholderClass =
    'min-h-16 rounded-xl border border-dashed border-slate-400 bg-slate-100';
  readonly dragPreviewChipClass =
    'box-border inline-flex max-w-64 items-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-semibold leading-5 text-slate-900 shadow-[0_4px_12px_rgb(15_23_42/0.12)] pointer-events-none will-change-transform';
  readonly canvasBackgroundClass = 'bg-slate-50';
  readonly workflowZoomAnimatedClass =
    'transition-[transform,width,height,background-size] duration-[160ms] ease-[cubic-bezier(0.22,1,0.36,1)]';
  readonly previewDragging = signal(false);
  readonly paletteDragItem = signal<FieldCatalogItem | null>(null);
  readonly paletteGhostX = signal(0);
  readonly paletteGhostY = signal(0);
  readonly paletteDropIndex = signal<number | null>(null);

  private static readonly PALETTE_DRAG_THRESHOLD_PX = 6;
  private static readonly PALETTE_AUTO_SCROLL_EDGE_PX = 72;
  private static readonly PALETTE_AUTO_SCROLL_MAX_SPEED_PX = 12;
  private paletteSuppressCatalogClick = false;
  private palettePointerCleanup: (() => void) | null = null;
  private paletteAutoScrollRafId: number | null = null;
  private palettePointerX = 0;
  private palettePointerY = 0;
  /** Kept for easy restore; not used while Workflow is gated off. */
  readonly allBuilderSteps = ALL_BUILDER_STEPS;
  readonly workflowStepEnabled = WORKFLOW_STEP_ENABLED;
  readonly numberedStepperEnabled = NUMBERED_STEPPER_ENABLED;
  readonly builderSteps: BuilderStepMeta[] = WORKFLOW_STEP_ENABLED
    ? ALL_BUILDER_STEPS
    : ALL_BUILDER_STEPS.filter((step) => step.id !== 'workflow');

  readonly builderCompareTabs: BuilderCompareTab[] = WORKFLOW_STEP_ENABLED
    ? BUILDER_COMPARE_TABS
    : BUILDER_COMPARE_TABS.filter((tab) => tab.id !== 'workflow');

  readonly builderStep = signal<BuilderStep>('form');
  readonly builderMode = signal<BuilderMode>('manual');
  readonly componentsPanelOpen = signal(false);
  /** Mobile: left Insert/AI panel overlay (hidden by default below lg). */
  readonly mobileLeftOpen = signal(false);
  readonly messages = signal<ChatMessage[]>([]);
  readonly builtForm = signal<BuiltForm | null>(createEmptyBuiltForm());
  readonly workflowActions = signal<WorkflowAction[]>([]);
  readonly formPublishConfig = signal<FormPublishConfig>(createDefaultFormPublishConfig());
  readonly prompt = signal('');
  readonly canvasPrompt = signal('');
  readonly isGenerating = signal(false);
  readonly previewMode = signal<'desktop' | 'mobile'>('desktop');
  readonly paletteSearch = signal('');
  readonly selectedFieldId = signal<string | null>(null);
  readonly selectedWorkflowActionId = signal<string | null>(null);
  readonly activePageId = signal<string>(DEFAULT_PAGE_ID);
  readonly configTab = signal<'page' | 'field'>('page');
  readonly formSettingsOpen = signal(false);
  readonly workflowTriggerPosition = signal<WorkflowPosition>({ ...WORKFLOW_TRIGGER_POSITION });
  readonly addBubblePosition = signal<WorkflowPosition>(
    this.bubblePositionBelow(WORKFLOW_TRIGGER_POSITION, WORKFLOW_ACTION_HEIGHT),
  );
  readonly branchBubbles = signal<Record<string, BranchBubblePair>>({});
  readonly actionPickerOpen = signal(false);
  readonly actionPickerAnchor = signal<WorkflowPosition | null>(null);
  readonly actionPickerScreen = signal<WorkflowPosition | null>(null);
  readonly actionPickerTarget = signal<WorkflowPickerTarget | null>(null);
  readonly workflowZoom = signal(1);
  readonly zoomAnimated = signal(true);
  private readonly workflowMinCanvasWidth = signal(640);
  private readonly dragOverride = signal<{ id: string; x: number; y: number } | null>(null);
  private addBubbleDidDrag = false;
  private branchBubbleDidDrag = false;
  private workflowNodeDidDrag = false;
  private zoomAnimationTimer: ReturnType<typeof setTimeout> | null = null;
  private autosaveTimer: ReturnType<typeof setTimeout> | null = null;
  /** Form restored from the preview handoff; the following GET must not replace it. */
  private restoredPreviewDraft = false;
  private loadInProgress = false;
  private saveSequence = 0;

  readonly hasConversation = computed(() => this.messages().length > 0);
  readonly workflowZoomPercent = computed(() => Math.round(this.workflowZoom() * 100));
  readonly previewStatus = computed(() => (this.builtForm() ? 'Draft' : 'Empty'));
  readonly hasWorkflowActions = computed(() => this.workflowActions().length > 0);
  readonly currentStepIndex = computed(() => this.stepIndex(this.builderStep()));
  readonly filteredAudienceGroups = computed(() => {
    const query = this.audienceSearch().trim().toLowerCase();
    const groups = this.audienceGroups();
    if (!query) {
      return groups;
    }
    return groups.filter((group) => group.name.toLowerCase().includes(query));
  });
  readonly filteredAudienceContacts = computed(() => {
    const query = this.audienceSearch().trim().toLowerCase();
    const contacts = this.audienceContacts();
    if (!query) {
      return contacts;
    }
    return contacts.filter((contact) => {
      const name = this.contactDisplayName(contact).toLowerCase();
      return name.includes(query) || contact.email.toLowerCase().includes(query);
    });
  });
  readonly layoutComponents = computed(() =>
    this.filterCatalogItems(LAYOUT_FIELD_CATALOG, this.paletteSearch()),
  );
  readonly basicComponents = computed(() =>
    this.filterCatalogItems(BASIC_FIELD_CATALOG, this.paletteSearch()),
  );
  readonly intermediateComponents = computed(() =>
    this.filterCatalogItems(INTERMEDIATE_FIELD_CATALOG, this.paletteSearch()),
  );
  readonly advancedComponents = computed(() =>
    this.filterCatalogItems(ADVANCED_FIELD_CATALOG, this.paletteSearch()),
  );
  readonly paletteHasMatches = computed(
    () =>
      this.layoutComponents().length > 0 ||
      this.basicComponents().length > 0 ||
      this.intermediateComponents().length > 0 ||
      this.advancedComponents().length > 0,
  );
  readonly ifElseActions = computed(() =>
    this.workflowActions().filter((action) => action.type === 'if_else'),
  );
  readonly showRootAddBubble = computed(() => {
    const roots = rootActions(this.workflowActions());
    if (roots.length === 0) {
      return true;
    }
    return roots[roots.length - 1].type !== 'if_else';
  });
  readonly orderedPages = computed(() =>
    sortedPages(this.builtForm()?.pages ?? []),
  );
  readonly activePage = computed(() =>
    findPage(this.builtForm(), this.activePageId()),
  );
  readonly activePageFields = computed(() => this.activePage()?.fields ?? []);
  readonly canDeleteActivePage = computed(() => this.orderedPages().length > 1);
  readonly previewFields = computed(() => allFormFields(this.builtForm()));
  readonly selectedField = computed(() => {
    const fieldId = this.selectedFieldId();
    const page = this.activePage();
    if (!fieldId || !page) {
      return null;
    }
    return page.fields.find((field) => field.id === fieldId) ?? null;
  });
  readonly selectedWorkflowAction = computed(() => {
    const actionId = this.selectedWorkflowActionId();
    if (!actionId) {
      return null;
    }
    return this.workflowActions().find((action) => action.id === actionId) ?? null;
  });
  readonly workspaceSlug = computed(
    () => this.tokenStorage.getWorkspace()?.workspaceSlug?.trim() || '',
  );
  readonly publishSlugPrefix = computed(() => {
    const origin =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'https://forms.formops.app';
    const ws = this.workspaceSlug() || 'workspace';
    return `${origin}/${ws}/`;
  });
  readonly publishSlugCopied = signal(false);
  private publishSlugCopyTimer: ReturnType<typeof setTimeout> | null = null;
  readonly publishActionLabel = computed(() => {
    if (this.publishing()) {
      return 'Publishing…';
    }
    return this.formStatus() === 'published' ? 'Republish' : 'Publish';
  });
  readonly workflowCanvasSize = computed(() => {
    const actions = this.workflowActions();
    const trigger = this.workflowTriggerPosition();
    let maxX = trigger.x + WORKFLOW_NODE_WIDTH;
    let maxY = trigger.y + WORKFLOW_ACTION_HEIGHT;

    for (const action of actions) {
      const position = this.livePosition(action.id, action);
      maxX = Math.max(maxX, position.x + WORKFLOW_NODE_WIDTH);
      maxY = Math.max(maxY, position.y + this.workflowNodeHeight(action));
    }

    if (this.showRootAddBubble()) {
      const bubble = this.livePosition('add-bubble', this.addBubblePosition());
      maxX = Math.max(maxX, bubble.x + WORKFLOW_BUBBLE_SIZE);
      maxY = Math.max(maxY, bubble.y + WORKFLOW_BUBBLE_SIZE);
    }

    for (const [ifElseId, pair] of Object.entries(this.branchBubbles())) {
      const thenPos = this.livePosition(this.branchBubbleId(ifElseId, 'then'), pair.then);
      const elsePos = this.livePosition(this.branchBubbleId(ifElseId, 'else'), pair.else);
      maxX = Math.max(maxX, thenPos.x + WORKFLOW_BUBBLE_SIZE, elsePos.x + WORKFLOW_BUBBLE_SIZE);
      maxY = Math.max(maxY, thenPos.y + WORKFLOW_BUBBLE_SIZE, elsePos.y + WORKFLOW_BUBBLE_SIZE);
    }

    return {
      width: Math.max(640, this.workflowMinCanvasWidth(), maxX + 120),
      height: Math.max(480, maxY + 160),
    };
  });
  readonly workflowEdges = computed((): WorkflowEdge[] => {
    const actions = this.workflowActions();
    const roots = rootActions(actions);
    const trigger = this.workflowTriggerPosition();
    const rootBubble = this.livePosition('add-bubble', this.addBubblePosition());
    const showRootBubble = this.showRootAddBubble();
    const edges: WorkflowEdge[] = [];
    let edgeIndex = 0;

    const pushEdge = (from: WorkflowBox, to: WorkflowBox, fromPortY?: number) => {
      const x1 = from.x + from.w / 2;
      const y1 = fromPortY ?? from.y + from.h;
      const x2 = to.x + to.w / 2;
      const y2 = to.y;
      const midY = (y1 + y2) / 2;
      edges.push({
        id: `workflow-edge-${edgeIndex}`,
        d: `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`,
      });
      edgeIndex += 1;
    };

    const actionBox = (action: WorkflowAction): WorkflowBox => {
      const position = this.livePosition(action.id, action);
      return {
        x: position.x,
        y: position.y,
        w: WORKFLOW_NODE_WIDTH,
        h: this.workflowNodeHeight(action),
      };
    };

    const bubbleBox = (id: string, position: WorkflowPosition): WorkflowBox => {
      const live = this.livePosition(id, position);
      return { x: live.x, y: live.y, w: WORKFLOW_BUBBLE_SIZE, h: WORKFLOW_BUBBLE_SIZE };
    };

    const triggerBox: WorkflowBox = {
      x: trigger.x,
      y: trigger.y,
      w: WORKFLOW_NODE_WIDTH,
      h: WORKFLOW_ACTION_HEIGHT,
    };

    if (roots.length === 0) {
      pushEdge(triggerBox, bubbleBox('add-bubble', rootBubble));
    } else {
      pushEdge(triggerBox, actionBox(roots[0]));
      for (let index = 0; index < roots.length - 1; index += 1) {
        pushEdge(actionBox(roots[index]), actionBox(roots[index + 1]));
      }
      if (showRootBubble) {
        pushEdge(actionBox(roots[roots.length - 1]), bubbleBox('add-bubble', rootBubble));
      }
    }

    for (const ifElse of actions.filter((action) => action.type === 'if_else')) {
      const ifPos = this.livePosition(ifElse.id, ifElse);
      const ifBox: WorkflowBox = {
        x: ifPos.x,
        y: ifPos.y,
        w: WORKFLOW_NODE_WIDTH,
        h: WORKFLOW_BRANCH_HEIGHT,
      };
      const pair = this.branchBubbles()[ifElse.id] ?? this.defaultBranchBubbles(ifElse);
      const thenKids = branchActions(actions, ifElse.id, 'then');
      const elseKids = branchActions(actions, ifElse.id, 'else');

      const chainBranch = (
        branch: WorkflowBranch,
        kids: WorkflowAction[],
        portY: number,
        bubblePos: WorkflowPosition,
      ) => {
        const bubbleId = this.branchBubbleId(ifElse.id, branch);
        const end = bubbleBox(bubbleId, bubblePos);
        if (kids.length === 0) {
          pushEdge(ifBox, end, ifBox.y + portY);
          return;
        }
        pushEdge(ifBox, actionBox(kids[0]), ifBox.y + portY);
        for (let index = 0; index < kids.length - 1; index += 1) {
          pushEdge(actionBox(kids[index]), actionBox(kids[index + 1]));
        }
        pushEdge(actionBox(kids[kids.length - 1]), end);
      };

      chainBranch('then', thenKids, WORKFLOW_IF_THEN_PORT_Y, pair.then);
      chainBranch('else', elseKids, WORKFLOW_IF_ELSE_PORT_Y, pair.else);
    }

    return edges;
  });

  constructor() {
    super();
    effect((onCleanup) => {
      if (this.builderStep() !== 'workflow') {
        return;
      }

      const viewport = this.workflowViewport()?.nativeElement;
      if (!viewport) {
        return;
      }

      if (this.workflowActions().length === 0) {
        queueMicrotask(() => this.centerWorkflowTrigger());
      }

      const onWheel = (event: WheelEvent) => this.onWorkflowWheel(event);
      viewport.addEventListener('wheel', onWheel, { passive: false });
      onCleanup(() => viewport.removeEventListener('wheel', onWheel));
    });
  }

  ngOnInit(): void {
    this.previousSidebarExpanded = this.layoutService.sidebarExpanded;
    this.layoutService.setSidebarExpanded(false);
    this.loadFormDefinition(() => this.applyCreateFormEntry());
  }

  private loadFormDefinition(onReady?: () => void): void {
    const slug = this.formSlug().trim();
    if (!slug) {
      onReady?.();
      return;
    }

    const draft = this.formPreviewHandoff.takeForSlug(slug);
    if (draft) {
      this.restoredPreviewDraft = true;
      this.builtForm.set(draft.form);
      this.previewMode.set(draft.previewMode);
      this.activePageId.set(
        sortedPages(draft.form.pages)[0]?.id ?? DEFAULT_PAGE_ID,
      );
    }

    this.loadInProgress = true;
    this.executeRequest({
      state: this.loadFormState,
      request: this.userAPIService.getFormDetails({ slug }),
      handleError: true,
      onSuccess: (response) => {
        const loaded = mapBuiltFormDtoToBuiltForm(response.data.definition);
        if (this.restoredPreviewDraft) {
          this.enrichVariableDefaults(this.builtForm() ?? loaded);
        } else {
          this.builtForm.set(loaded);
          this.enrichVariableDefaults(loaded);
          this.activePageId.set(
            sortedPages(loaded.pages)[0]?.id ?? DEFAULT_PAGE_ID,
          );
        }
        const formSlug = response.data.slug?.trim() || slug;
        const status = response.data.status;
        if (
          status === 'draft' ||
          status === 'published' ||
          status === 'unpublished' ||
          status === 'finished'
        ) {
          this.formStatus.set(status);
        }

        const settings = response.data.publishSettings;
        this.hasAccessPassword.set(Boolean(settings?.hasAccessPassword));

        this.formPublishConfig.update((current) => {
          if (!settings) {
            return {
              ...current,
              customSlug: formSlug,
              accessPassword: '',
            };
          }

          const maxSubmissions =
            typeof settings.maxSubmissions === 'number'
              ? String(settings.maxSubmissions)
              : '';

          return {
            ...current,
            customSlug: formSlug,
            visibility: settings.visibility as FormVisibility,
            accessPassword: '',
            allowedContactIds: settings.allowedContactIds ?? [],
            allowedGroupIds: settings.allowedGroupIds ?? [],
            invitedEmails: [],
            publishTiming: settings.publishTiming as PublishTiming,
            scheduledAt: toDatetimeLocalValue(
              typeof settings.scheduledAt === 'string'
                ? settings.scheduledAt
                : null,
            ),
            closeOnScheduleEnd: settings.closeOnScheduleEnd ?? false,
            scheduledEndAt: toDatetimeLocalValue(
              typeof settings.scheduledEndAt === 'string'
                ? settings.scheduledEndAt
                : null,
            ),
            requireSubmissionApproval:
              settings.requireSubmissionApproval ?? false,
            maxSubmissions,
            formSubmissionDraftRetentionHours: draftRetentionOptionFromHours(
              typeof settings.formSubmissionDraftRetentionHours === 'number'
                ? settings.formSubmissionDraftRetentionHours
                : undefined,
            ),
            submissionReferenceMode: submissionReferenceModeFromValue(
              settings.submissionReferenceMode,
            ),
            submissionReferencePrefix:
              typeof settings.submissionReferencePrefix === 'string'
                ? settings.submissionReferencePrefix
                : current.submissionReferencePrefix,
            submissionReferenceDigitLength:
              submissionReferenceDigitLengthFromValue(
                settings.submissionReferenceDigitLength,
              ),
            submissionReferencePattern:
              typeof settings.submissionReferencePattern === 'string' &&
              settings.submissionReferencePattern.trim()
                ? settings.submissionReferencePattern
                : current.submissionReferencePattern,
          };
        });

        if (settings?.visibility === 'workspace') {
          this.ensureAudienceLoaded();
        }

        this.loadInProgress = false;
        onReady?.();
      },
      onFailed: () => {
        this.loadInProgress = false;
        onReady?.();
      },
    });
  }

  private enrichVariableDefaults(form: BuiltForm): void {
    const hasVariableDefault = allFormFields(form).some(
      (field) => field.default?.source === 'variable',
    );
    const hasCollectionOptions = allFormFields(form).some(
      (field) => Boolean(field.dataLibraryCollection?.dataLibraryId),
    );
    if (!hasVariableDefault && !hasCollectionOptions) {
      return;
    }

    this.userAPIService
      .listDataLibrary({
        pageNumber: 1,
        pageSize: 100,
      })
      .subscribe({
        next: (response) => {
          const items = response.data?.items ?? [];
          this.builtForm.update((current) => {
            if (!current) {
              return current;
            }
            let next = current;
            if (hasVariableDefault) {
              next = enrichBuiltFormVariableDefaults(next, items);
            }
            if (hasCollectionOptions) {
              next = enrichBuiltFormCollectionRefs(next, items);
            }
            return next;
          });
        },
      });
  }

  private applyCreateFormEntry(): void {
    const modeParam = this.route.snapshot.queryParamMap.get('mode');
    const mode: BuilderMode = modeParam === 'ai' ? 'ai' : 'manual';
    this.setBuilderMode(mode);
    this.applyStepQueryParam();

    const initialPrompt = this.createFormAiHandoff.consume();
    if (!initialPrompt) {
      return;
    }

    this.setBuilderMode('ai');
    this.prompt.set(initialPrompt);
    queueMicrotask(() => this.generate());
  }

  private applyStepQueryParam(): void {
    const stepParam = this.route.snapshot.queryParamMap.get('step');
    if (stepParam === 'form' || stepParam === 'workflow' || stepParam === 'config') {
      this.setBuilderStep(stepParam);
    }
  }

  ngOnDestroy(): void {
    if (this.zoomAnimationTimer != null) {
      clearTimeout(this.zoomAnimationTimer);
      this.zoomAnimationTimer = null;
    }
    this.flushAutosave();
    if (this.publishSlugCopyTimer != null) {
      clearTimeout(this.publishSlugCopyTimer);
      this.publishSlugCopyTimer = null;
    }
    this.layoutService.setSidebarExpanded(this.previousSidebarExpanded);
    this.detachPalettePointerListeners();
    this.stopPaletteAutoScrollLoop();
    this.paletteDragItem.set(null);
    this.paletteDropIndex.set(null);
    this.previewDragging.set(false);
  }

  stepIndex(step: BuilderStep): number {
    return BUILDER_STEP_ORDER.indexOf(step);
  }

  isStepCurrent(step: BuilderStep): boolean {
    return this.builderStep() === step;
  }

  isStepComplete(step: BuilderStep): boolean {
    return this.stepIndex(step) < this.currentStepIndex();
  }

  previewControlClass = previewControlClass;
  readonly themeCssVars = themeCssVars;
  readonly themeAccentHex = themeAccentHex;

  previewSelectionClass(selected: boolean): string {
    return selected
      ? 'border-primary-500 ring-2 ring-primary-500/20'
      : 'border-transparent';
  }

  paletteIconToneClass(itemId: string): string {
    switch (itemId) {
      case 'heading':
        return 'text-violet-600';
      case 'paragraph':
        return 'text-teal-600';
      case 'divider':
        return 'text-slate-600';
      case 'spacer':
        return 'text-sky-600';
      case 'text':
        return 'text-blue-600';
      case 'textarea':
      case 'rich_text':
        return 'text-orange-600';
      case 'email':
        return 'text-sky-600';
      case 'phone':
        return 'text-emerald-600';
      case 'number':
      case 'numeric_stepper':
      case 'number_grid':
        return 'text-rose-600';
      case 'url':
        return 'text-indigo-600';
      case 'currency':
        return 'text-amber-700';
      case 'checkbox':
      case 'checkbox_group':
        return 'text-blue-600';
      case 'radio_group':
      case 'single_choice':
      case 'multi_choice':
      case 'single_choice_grid':
      case 'multi_choice_grid':
      case 'select':
      case 'searchable_picker':
      case 'switch':
        return 'text-violet-600';
      case 'date':
      case 'datetime':
      case 'date_range':
        return 'text-blue-600';
      case 'time':
      case 'time_range':
        return 'text-violet-600';
      case 'slider':
      case 'rating':
      case 'range_slider':
        return 'text-sky-600';
      case 'color':
        return 'text-pink-600';
      case 'image_picker':
      case 'camera':
        return 'text-cyan-600';
      case 'live_geo_coordinates':
      case 'map':
        return 'text-fuchsia-600';
      case 'address_selector':
        return 'text-green-600';
      case 'file_uploader':
      case 'document_scanner':
        return 'text-orange-600';
      case 'audio_recorder':
      case 'video_recorder':
        return 'text-rose-600';
      case 'signature':
      case 'drawing':
        return 'text-amber-700';
      case 'captcha':
        return 'text-slate-600';
      default:
        return 'text-slate-600';
    }
  }

  isCompareTabActive(tab: BuilderCompareTab): boolean {
    return tab.id !== 'integrations' && this.builderStep() === tab.id;
  }

  onCompareTabClick(tab: BuilderCompareTab): void {
    if (tab.disabled || tab.id === 'integrations') {
      return;
    }
    this.setBuilderStep(tab.id);
  }

  toggleMobileLeftPanel(): void {
    this.mobileLeftOpen.update((open) => !open);
  }

  closeMobileLeftPanel(): void {
    this.mobileLeftOpen.set(false);
  }

  setBuilderStep(step: BuilderStep): void {
    if (!WORKFLOW_STEP_ENABLED && step === 'workflow') {
      this.builderStep.set('config');
      this.componentsPanelOpen.set(false);
      this.mobileLeftOpen.set(false);
      this.ensureAudienceLoaded();
      this.syncStepQueryParam('config');
      return;
    }

    this.builderStep.set(step);
    this.mobileLeftOpen.set(false);
    if (step !== 'form') {
      this.componentsPanelOpen.set(false);
    }

    if (step === 'config') {
      this.ensureAudienceLoaded();
    }

    if (step === 'workflow' && this.workflowActions().length === 0) {
      queueMicrotask(() => {
        requestAnimationFrame(() => this.centerWorkflowTrigger());
      });
    }

    this.syncStepQueryParam(step);
  }

  private syncStepQueryParam(step: BuilderStep): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  centerWorkflowTrigger(): void {
    if (this.workflowActions().length > 0) {
      return;
    }

    const viewport = this.workflowViewport()?.nativeElement;
    if (!viewport) {
      return;
    }

    const contentWidth = Math.max(viewport.clientWidth - 48, WORKFLOW_NODE_WIDTH);
    this.workflowMinCanvasWidth.set(contentWidth);
    const x = Math.max(24, (contentWidth - WORKFLOW_NODE_WIDTH) / 2);
    const y = 40;
    this.workflowTriggerPosition.set({ x, y });
    this.repositionAddBubble();
    viewport.scrollLeft = Math.max(
      0,
      (x + WORKFLOW_NODE_WIDTH / 2) * this.workflowZoom() - viewport.clientWidth / 2,
    );
    viewport.scrollTop = 0;
  }

  continueToWorkflow(): void {
    if (!WORKFLOW_STEP_ENABLED) {
      this.setBuilderStep('config');
      return;
    }
    this.setBuilderStep('workflow');
  }

  continueToConfiguration(): void {
    this.setBuilderStep('config');
  }

  updatePublishConfig<K extends keyof FormPublishConfig>(
    key: K,
    value: FormPublishConfig[K],
  ): void {
    this.formPublishConfig.update((current) => {
      let nextValue: FormPublishConfig[K] = value;
      // type=number ngModel emits number | null; keep FormPublishConfig as string.
      if (key === 'maxSubmissions') {
        nextValue = (
          value == null || value === '' ? '' : String(value)
        ) as FormPublishConfig[K];
      }
      const next = { ...current, [key]: nextValue };
      if (key === 'closeOnScheduleEnd' && value === false) {
        next.scheduledEndAt = '';
      }
      return next;
    });
  }

  onDraftRetentionChange(value: unknown): void {
    this.updatePublishConfig(
      'formSubmissionDraftRetentionHours',
      draftRetentionOptionFromHours(
        typeof value === 'string' || typeof value === 'number'
          ? Number(value)
          : undefined,
      ),
    );
  }

  onSubmissionReferenceDigitLengthChange(value: unknown): void {
    this.updatePublishConfig(
      'submissionReferenceDigitLength',
      submissionReferenceDigitLengthFromValue(value),
    );
  }

  onSubmissionReferenceModeChange(mode: SubmissionReferenceMode): void {
    const next = submissionReferenceModeFromValue(mode);
    this.formPublishConfig.update((current) => {
      const pattern =
        current.submissionReferencePattern.trim() ||
        defaultCustomSubmissionReferencePattern(
          current.submissionReferencePrefix,
          current.submissionReferenceDigitLength,
        );
      return {
        ...current,
        submissionReferenceMode: next,
        submissionReferencePattern: pattern,
      };
    });
  }

  insertSubmissionReferenceToken(token: string): void {
    const current = this.formPublishConfig().submissionReferencePattern ?? '';
    const next = `${current}${token}`;
    this.updatePublishConfig('submissionReferencePattern', next);
    queueMicrotask(() => {
      this.submissionReferencePatternField()?.focusHandlebars(next.length);
    });
  }

  submissionReferencePreview(): string {
    return resolveSubmissionReferencePreview(this.formPublishConfig(), 123);
  }

  submissionReferencePatternHasSeq(): boolean {
    return hasValidSubmissionReferenceSeqToken(
      this.formPublishConfig().submissionReferencePattern,
    );
  }

  onVisibilityChange(value: FormVisibility): void {
    this.updatePublishConfig('visibility', value);
    if (value === 'workspace') {
      this.ensureAudienceLoaded();
    }
  }

  setAudienceTab(tab: 'groups' | 'contacts'): void {
    this.audienceTab.set(tab);
  }

  onAudienceSearchChange(value: string): void {
    this.audienceSearch.set(value);
  }

  onAudienceInviteDraftChange(value: string): void {
    this.audienceInviteDraft.set(value);
  }

  sendAudienceInvites(): void {
    if (this.audienceInviting()) {
      return;
    }

    const raw = this.audienceInviteDraft().trim();
    if (!raw) {
      this.toasterService.error('Enter at least one email address.');
      return;
    }

    const parts = [
      ...new Set(
        raw
          .split(/[\s,;]+/)
          .map((part) => part.trim().toLowerCase())
          .filter(Boolean),
      ),
    ];

    if (parts.length === 0) {
      this.toasterService.error('Enter at least one email address.');
      return;
    }

    const invalid = parts.filter((email) => !AUDIENCE_EMAIL_PATTERN.test(email));
    if (invalid.length > 0) {
      this.toasterService.error(
        invalid.length === 1
          ? `Invalid email: ${invalid[0]}`
          : `Invalid emails: ${invalid.slice(0, 3).join(', ')}${invalid.length > 3 ? '…' : ''}`,
      );
      return;
    }

    if (parts.length > 100) {
      this.toasterService.error('You can add up to 100 emails at a time.');
      return;
    }

    this.audienceInviting.set(true);
    this.audienceInviteDraft.set('');

    this.executeRequest({
      state: this.audienceInviteState,
      request: this.userAPIService.createContacts({
        contacts: parts.map((email) => ({
          firstName: deriveContactFirstName(email),
          email,
        })),
      }),
      handleError: true,
      onSuccess: (response) => {
        this.audienceInviting.set(false);

        const created = response.data?.items ?? [];
        const existing = response.data?.existingItems ?? [];
        const createdCount = response.data?.createdCount ?? created.length;
        const existingCount = response.data?.skippedCount ?? existing.length;

        for (const contact of [...created, ...existing]) {
          this.upsertAudienceContact(contact);
          this.toggleAllowedContact(contact.id, true);
        }

        // Durable access is allowedContactIds; drop invite chips for resolved emails.
        this.formPublishConfig.update((current) => ({
          ...current,
          invitedEmails: current.invitedEmails.filter(
            (item) => !parts.includes(item.toLowerCase()),
          ),
        }));

        this.audienceTab.set('contacts');

        if (createdCount > 0 && existingCount > 0) {
          this.toasterService.success(
            `${createdCount} new, ${existingCount} already in contacts`,
          );
        } else if (createdCount > 0) {
          this.toasterService.success(
            createdCount === 1
              ? 'Contact created'
              : `${createdCount} contacts created`,
          );
        } else if (existingCount > 0) {
          this.toasterService.success(
            existingCount === 1
              ? 'Contact already in your list'
              : `${existingCount} contacts already in your list`,
          );
        }
      },
      onFailed: () => {
        this.audienceInviting.set(false);
        // Restore draft so the user can retry without retyping.
        this.audienceInviteDraft.set(parts.join(', '));
      },
    });
  }

  removeInvitedEmail(email: string): void {
    this.formPublishConfig.update((current) => ({
      ...current,
      invitedEmails: current.invitedEmails.filter((item) => item !== email),
    }));
  }

  openAddPeople(): void {
    const dialogRef = this.overlayService.openModal(CreateContactOverlayComponent);

    dialogRef.closed.subscribe(
      (result: CreateContactOverlayResult | null | undefined) => {
        if (!result) {
          return;
        }
        this.audienceContacts.update((contacts) => {
          if (contacts.some((contact) => contact.id === result.id)) {
            return contacts;
          }
          return [result, ...contacts];
        });
        this.toggleAllowedContact(result.id, true);
        this.audienceTab.set('contacts');
        this.loadAudienceContacts();
      },
    );
  }

  isContactAllowed(id: string): boolean {
    return this.formPublishConfig().allowedContactIds.includes(id);
  }

  isGroupAllowed(id: string): boolean {
    return this.formPublishConfig().allowedGroupIds.includes(id);
  }

  toggleAllowedContact(id: string, selected: boolean): void {
    this.formPublishConfig.update((current) => {
      const next = new Set(current.allowedContactIds);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return { ...current, allowedContactIds: [...next] };
    });
  }

  toggleAllowedGroup(id: string, selected: boolean): void {
    this.formPublishConfig.update((current) => {
      const next = new Set(current.allowedGroupIds);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return { ...current, allowedGroupIds: [...next] };
    });
  }

  contactDisplayName(contact: ContactResponse): string {
    return [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.email;
  }

  audienceAvatarColor(id: string): string {
    return resolveAvatarColorClass(id);
  }

  audienceInitials(source: {
    firstName?: string | null;
    lastName?: string | null;
    name?: string | null;
    email?: string | null;
  }): string {
    return resolveInitials(source);
  }

  private upsertAudienceContact(contact: ContactResponse): void {
    this.audienceContacts.update((contacts) => {
      if (contacts.some((item) => item.id === contact.id)) {
        return contacts.map((item) => (item.id === contact.id ? contact : item));
      }
      return [contact, ...contacts];
    });
  }

  private ensureAudienceLoaded(): void {
    if (this.audienceLoaded) {
      return;
    }
    this.audienceLoaded = true;
    this.loadAudienceContacts();
    this.loadAudienceGroups();
  }

  private loadAudienceContacts(): void {
    this.executeRequest({
      state: this.audienceContactsState,
      request: this.userAPIService.listContacts({
        pageNumber: 1,
        pageSize: 100,
      }),
      handleError: true,
      onSuccess: (response) => {
        this.audienceContacts.set(response.data?.items ?? []);
      },
    });
  }

  private loadAudienceGroups(): void {
    this.executeRequest({
      state: this.audienceGroupsState,
      request: this.userAPIService.listContactGroups({
        pageNumber: 1,
        pageSize: 100,
      }),
      handleError: true,
      onSuccess: (response) => {
        this.audienceGroups.set(response.data?.items ?? []);
      },
    });
  }

  async copyPublishSlugPreview(): Promise<void> {
    const origin =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'https://forms.formops.app';
    const url = buildPublishedFormAbsoluteUrl(
      this.workspaceSlug(),
      this.formPublishConfig().customSlug || this.formSlug(),
      origin,
    );
    try {
      await navigator.clipboard.writeText(url);
      this.publishSlugCopied.set(true);
      if (this.publishSlugCopyTimer != null) {
        clearTimeout(this.publishSlugCopyTimer);
      }
      this.publishSlugCopyTimer = setTimeout(() => {
        this.publishSlugCopied.set(false);
        this.publishSlugCopyTimer = null;
      }, 1800);
    } catch {
      // Clipboard may be unavailable; leave UI unchanged.
    }
  }

  publishForm(): void {
    const currentSlug = this.formSlug().trim();
    if (!currentSlug) {
      this.toasterService.error('Form slug is missing.');
      return;
    }

    const config = this.formPublishConfig();

    if (config.visibility === 'workspace') {
      if (
        config.allowedContactIds.length === 0 &&
        config.allowedGroupIds.length === 0
      ) {
        this.toasterService.error(
          'Select at least one contact or group for specific people.',
        );
        return;
      }
    }

    if (config.visibility === 'password_protected') {
      const password = config.accessPassword.trim();
      if (!password && !this.hasAccessPassword()) {
        this.toasterService.error('Enter an access password.');
        return;
      }
    }

    if (config.publishTiming === 'scheduled' && !config.scheduledAt.trim()) {
      this.toasterService.error('Choose a publish date and time.');
      return;
    }

    if (config.closeOnScheduleEnd && !config.scheduledEndAt.trim()) {
      this.toasterService.error('Choose a close date and time.');
      return;
    }

    const maxRaw = String(config.maxSubmissions ?? '').trim();
    let maxSubmissions: number | null = null;
    if (maxRaw) {
      const parsed = Number.parseInt(maxRaw, 10);
      if (!Number.isFinite(parsed) || parsed < 1) {
        this.toasterService.error('Max submissions must be a positive number.');
        return;
      }
      maxSubmissions = parsed;
    }

    const formSubmissionDraftRetentionHours = draftRetentionHoursFromConfig(
      config.formSubmissionDraftRetentionHours,
    );

    const referenceError = getSubmissionReferencePublishError(config);
    if (referenceError) {
      this.toasterService.error(referenceError);
      return;
    }

    const scheduledAt = toIsoFromDatetimeLocal(config.scheduledAt);
    const scheduledEndAt = toIsoFromDatetimeLocal(config.scheduledEndAt);

    this.formPublishConfig.update((current) => ({
      ...current,
      customSlug: currentSlug,
    }));
    this.publishing.set(true);

    this.executeRequest({
      state: this.publishFormState,
      request: this.userAPIService.publishForm(currentSlug, {
        visibility: config.visibility,
        ...(config.accessPassword.trim()
          ? { accessPassword: config.accessPassword.trim() }
          : {}),
        ...(config.visibility === 'workspace'
          ? {
              allowedContactIds: config.allowedContactIds,
              allowedGroupIds: config.allowedGroupIds,
            }
          : {
              allowedContactIds: [],
              allowedGroupIds: [],
            }),
        publishTiming: config.publishTiming,
        scheduledAt:
          config.publishTiming === 'scheduled' ? scheduledAt : null,
        closeOnScheduleEnd: config.closeOnScheduleEnd,
        scheduledEndAt: config.closeOnScheduleEnd ? scheduledEndAt : null,
        requireSubmissionApproval: config.requireSubmissionApproval,
        maxSubmissions,
        formSubmissionDraftRetentionHours,
        submissionReferenceMode: config.submissionReferenceMode,
        submissionReferencePrefix: config.submissionReferencePrefix.trim(),
        submissionReferenceDigitLength: config.submissionReferenceDigitLength,
        submissionReferencePattern: config.submissionReferencePattern.trim(),
      } as PublishFormRequest),
      handleError: true,
      showLoader: true,
      onSuccess: (response) => {
        this.publishing.set(false);
        this.formStatus.set('published');
        this.hasAccessPassword.set(
          Boolean(response.data.publishSettings?.hasAccessPassword),
        );
        this.formPublishConfig.update((current) => ({
          ...current,
          customSlug: response.data.slug,
          accessPassword: '',
        }));

        void this.router.navigate(['/forms/published', response.data.slug]);
      },
      onFailed: () => {
        this.publishing.set(false);
      },
    });
  }

  setBuilderMode(mode: BuilderMode): void {
    this.builderMode.set(mode);
    if (mode !== 'ai') {
      this.componentsPanelOpen.set(false);
    }
  }

  startWithAi(): void {
    this.setBuilderMode('ai');
    queueMicrotask(() => this.promptInput()?.nativeElement.focus());
  }

  setPreviewMode(mode: 'desktop' | 'mobile'): void {
    this.previewMode.set(mode);
  }

  togglePreviewMode(): void {
    this.previewMode.update((mode) => (mode === 'desktop' ? 'mobile' : 'desktop'));
  }

  goBackToForms(): void {
    void this.router.navigate(['/forms']);
  }

  openFormPreview(): void {
    const form = this.builtForm();
    const slug = this.formSlug().trim();
    if (!form || !slug) {
      return;
    }
    this.flushAutosave();
    this.formPreviewHandoff.set({
      slug,
      form,
      previewMode: this.previewMode(),
    });
    void this.router.navigate(['/forms/preview', slug]);
  }

  private filterCatalogItems(
    items: FieldCatalogItem[],
    query: string,
  ): FieldCatalogItem[] {
    const normalized = query.trim();
    if (!normalized) {
      return items;
    }
    return items.filter((item) => matchesFieldCatalogSearch(item, normalized));
  }

  addWorkflowAction(item: WorkflowActionCatalogItem): void {
    const target: WorkflowPickerTarget = { parentId: null, branch: null };
    const action = createActionFromCatalog(item, this.defaultNextPosition(target), target);
    this.workflowActions.update((actions) => [...actions, action]);
    this.selectedWorkflowActionId.set(action.id);
    this.closeActionPicker();
    this.repositionAllBubbles();
  }

  removeWorkflowAction(actionId: string, event?: Event): void {
    event?.stopPropagation();
    const actions = this.workflowActions();
    const removeIds = collectDescendantIds(actions, actionId);
    this.workflowActions.update((current) =>
      current.filter((action) => !removeIds.has(action.id)),
    );
    if (
      this.selectedWorkflowActionId() &&
      removeIds.has(this.selectedWorkflowActionId()!)
    ) {
      this.selectedWorkflowActionId.set(null);
    }
    this.branchBubbles.update((current) => {
      const next = { ...current };
      for (const id of removeIds) {
        delete next[id];
      }
      return next;
    });
    this.closeActionPicker();
    this.repositionAllBubbles();
  }

  selectWorkflowAction(action: WorkflowAction, event?: Event): void {
    event?.stopPropagation();
    if (this.workflowNodeDidDrag) {
      this.workflowNodeDidDrag = false;
      return;
    }
    this.closeActionPicker();
    this.selectedWorkflowActionId.set(action.id);
  }

  clearWorkflowSelection(): void {
    this.selectedWorkflowActionId.set(null);
  }

  actionKindLabel(action: WorkflowAction): 'Condition' | 'Action' {
    return action.type === 'if_else' ? 'Condition' : 'Action';
  }

  onWorkflowActionConfigChange(updated: WorkflowAction): void {
    this.workflowActions.update((actions) =>
      actions.map((item) => (item.id === updated.id ? updated : item)),
    );
  }

  onWorkflowNodeDragStarted(): void {
    this.workflowNodeDidDrag = false;
  }

  onWorkflowNodeDragMoved(action: WorkflowAction, event: CdkDragMove): void {
    this.workflowNodeDidDrag = true;
    const position = event.source.getFreeDragPosition();
    this.dragOverride.set({
      id: action.id,
      x: Math.max(0, position.x),
      y: Math.max(0, position.y),
    });
  }

  onWorkflowNodeDragEnded(action: WorkflowAction, event: CdkDragEnd): void {
    const position = event.source.getFreeDragPosition();
    this.onWorkflowNodeMoved(action.id, position.x, position.y);
    this.dragOverride.set(null);
    this.repositionAllBubbles();
  }

  onWorkflowNodeMoved(actionId: string, x: number, y: number): void {
    this.workflowActions.update((actions) =>
      actions.map((action) =>
        action.id === actionId
          ? { ...action, x: Math.max(0, x), y: Math.max(0, y) }
          : action,
      ),
    );
  }

  onAddBubbleDragStarted(): void {
    this.addBubbleDidDrag = false;
  }

  onAddBubbleDragMoved(event: CdkDragMove): void {
    this.addBubbleDidDrag = true;
    const position = event.source.getFreeDragPosition();
    this.dragOverride.set({
      id: 'add-bubble',
      x: Math.max(0, position.x),
      y: Math.max(0, position.y),
    });
  }

  onAddBubbleDragEnded(event: CdkDragEnd): void {
    const position = event.source.getFreeDragPosition();
    const next = { x: Math.max(0, position.x), y: Math.max(0, position.y) };
    this.addBubblePosition.set(next);
    this.dragOverride.set(null);
    this.openActionPicker(next, { parentId: null, branch: null });
  }

  onAddBubbleClick(event: Event): void {
    event.stopPropagation();
    if (this.addBubbleDidDrag) {
      this.addBubbleDidDrag = false;
      return;
    }
    this.openActionPicker(this.addBubblePosition(), { parentId: null, branch: null });
  }

  branchBubblePosition(ifElseId: string, branch: WorkflowBranch): WorkflowPosition {
    const pair = this.branchBubbles()[ifElseId];
    if (!pair) {
      const ifElse = this.workflowActions().find((action) => action.id === ifElseId);
      return ifElse
        ? this.defaultBranchBubbles(ifElse)[branch]
        : { x: 0, y: 0 };
    }
    return pair[branch];
  }

  onBranchBubbleDragStarted(): void {
    this.branchBubbleDidDrag = false;
  }

  onBranchBubbleDragMoved(
    ifElseId: string,
    branch: WorkflowBranch,
    event: CdkDragMove,
  ): void {
    this.branchBubbleDidDrag = true;
    const position = event.source.getFreeDragPosition();
    this.dragOverride.set({
      id: this.branchBubbleId(ifElseId, branch),
      x: Math.max(0, position.x),
      y: Math.max(0, position.y),
    });
  }

  onBranchBubbleDragEnded(
    ifElseId: string,
    branch: WorkflowBranch,
    event: CdkDragEnd,
  ): void {
    const position = event.source.getFreeDragPosition();
    const next = { x: Math.max(0, position.x), y: Math.max(0, position.y) };
    this.branchBubbles.update((current) => {
      const existing = current[ifElseId] ?? {
        then: next,
        else: next,
      };
      return {
        ...current,
        [ifElseId]: { ...existing, [branch]: next },
      };
    });
    this.dragOverride.set(null);
    this.openActionPicker(next, { parentId: ifElseId, branch });
  }

  onBranchBubbleClick(ifElseId: string, branch: WorkflowBranch, event: Event): void {
    event.stopPropagation();
    if (this.branchBubbleDidDrag) {
      this.branchBubbleDidDrag = false;
      return;
    }
    this.openActionPicker(this.branchBubblePosition(ifElseId, branch), {
      parentId: ifElseId,
      branch,
    });
  }

  onBranchRowClick(action: WorkflowAction, branch: WorkflowBranch, event: Event): void {
    event.stopPropagation();
    const portY = branch === 'then' ? WORKFLOW_IF_THEN_PORT_Y : WORKFLOW_IF_ELSE_PORT_Y;
    const anchor: WorkflowPosition = {
      x: action.x + WORKFLOW_NODE_WIDTH / 2 - WORKFLOW_BUBBLE_SIZE / 2,
      y: action.y + portY + 12,
    };
    this.openActionPicker(anchor, { parentId: action.id, branch });
  }

  openActionPicker(at: WorkflowPosition, target?: WorkflowPickerTarget): void {
    this.actionPickerTarget.set(target ?? { parentId: null, branch: null });
    this.actionPickerAnchor.set(at);
    this.actionPickerOpen.set(true);

    const stage = this.workflowStage()?.nativeElement;
    const spacer = this.workflowSpacer()?.nativeElement;
    if (!stage || !spacer) {
      this.actionPickerScreen.set({ x: 8, y: 8 });
      return;
    }

    const spacerRect = spacer.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();
    const zoom = this.workflowZoom();
    const x = spacerRect.left - stageRect.left + (at.x + WORKFLOW_BUBBLE_SIZE) * zoom + 12;
    const y = spacerRect.top - stageRect.top + at.y * zoom;
    this.actionPickerScreen.set({
      x: Math.max(8, Math.min(x, stageRect.width - WORKFLOW_PICKER_WIDTH - 8)),
      y: Math.max(8, Math.min(y, stageRect.height - WORKFLOW_PICKER_MAX_HEIGHT - 8)),
    });
  }

  closeActionPicker(): void {
    this.actionPickerOpen.set(false);
    this.actionPickerAnchor.set(null);
    this.actionPickerScreen.set(null);
    this.actionPickerTarget.set(null);
  }

  zoomWorkflowIn(): void {
    this.zoomAnimated.set(true);
    const stepped =
      Math.round((this.workflowZoom() + WORKFLOW_ZOOM_STEP) / WORKFLOW_ZOOM_STEP) *
      WORKFLOW_ZOOM_STEP;
    this.setWorkflowZoom(Number(stepped.toFixed(2)));
  }

  zoomWorkflowOut(): void {
    this.zoomAnimated.set(true);
    const stepped =
      Math.round((this.workflowZoom() - WORKFLOW_ZOOM_STEP) / WORKFLOW_ZOOM_STEP) *
      WORKFLOW_ZOOM_STEP;
    this.setWorkflowZoom(Number(stepped.toFixed(2)));
  }

  setWorkflowZoom(next: number, originClientX?: number, originClientY?: number): void {
    const viewport = this.workflowViewport()?.nativeElement;
    const oldZoom = this.workflowZoom();
    const newZoom = Math.min(WORKFLOW_ZOOM_MAX, Math.max(WORKFLOW_ZOOM_MIN, next));
    if (Math.abs(newZoom - oldZoom) < 0.0001) {
      return;
    }

    let originX: number | undefined;
    let originY: number | undefined;
    if (viewport) {
      const rect = viewport.getBoundingClientRect();
      originX =
        originClientX == null ? viewport.clientWidth / 2 : originClientX - rect.left;
      originY =
        originClientY == null ? viewport.clientHeight / 2 : originClientY - rect.top;
    }

    this.workflowZoom.set(newZoom);

    if (!viewport || originX == null || originY == null) {
      return;
    }

    queueMicrotask(() => {
      const contentX = (viewport.scrollLeft + originX) / oldZoom;
      const contentY = (viewport.scrollTop + originY) / oldZoom;
      viewport.scrollLeft = contentX * newZoom - originX;
      viewport.scrollTop = contentY * newZoom - originY;
    });
  }

  fitWorkflowToContent(): void {
    const viewport = this.workflowViewport()?.nativeElement;
    if (!viewport) {
      return;
    }

    this.zoomAnimated.set(true);

    const actions = this.workflowActions();
    const trigger = this.workflowTriggerPosition();
    let minX = trigger.x;
    let minY = trigger.y;
    let maxX = trigger.x + WORKFLOW_NODE_WIDTH;
    let maxY = trigger.y + WORKFLOW_ACTION_HEIGHT;

    for (const action of actions) {
      const position = this.livePosition(action.id, action);
      const height = this.workflowNodeHeight(action);
      minX = Math.min(minX, position.x);
      minY = Math.min(minY, position.y);
      maxX = Math.max(maxX, position.x + WORKFLOW_NODE_WIDTH);
      maxY = Math.max(maxY, position.y + height);
    }

    if (this.showRootAddBubble()) {
      const bubble = this.livePosition('add-bubble', this.addBubblePosition());
      minX = Math.min(minX, bubble.x);
      minY = Math.min(minY, bubble.y);
      maxX = Math.max(maxX, bubble.x + WORKFLOW_BUBBLE_SIZE);
      maxY = Math.max(maxY, bubble.y + WORKFLOW_BUBBLE_SIZE);
    }

    for (const [ifElseId, pair] of Object.entries(this.branchBubbles())) {
      const thenPos = this.livePosition(this.branchBubbleId(ifElseId, 'then'), pair.then);
      const elsePos = this.livePosition(this.branchBubbleId(ifElseId, 'else'), pair.else);
      minX = Math.min(minX, thenPos.x, elsePos.x);
      minY = Math.min(minY, thenPos.y, elsePos.y);
      maxX = Math.max(maxX, thenPos.x + WORKFLOW_BUBBLE_SIZE, elsePos.x + WORKFLOW_BUBBLE_SIZE);
      maxY = Math.max(maxY, thenPos.y + WORKFLOW_BUBBLE_SIZE, elsePos.y + WORKFLOW_BUBBLE_SIZE);
    }

    const bboxWidth = Math.max(1, maxX - minX);
    const bboxHeight = Math.max(1, maxY - minY);
    const availableWidth = Math.max(1, viewport.clientWidth - 48);
    const availableHeight = Math.max(1, viewport.clientHeight - 48);
    const scale = Math.min(
      WORKFLOW_ZOOM_MAX,
      Math.max(
        WORKFLOW_ZOOM_MIN,
        Math.min(availableWidth / bboxWidth, availableHeight / bboxHeight) * 0.9,
      ),
    );

    this.workflowZoom.set(scale);

    queueMicrotask(() => {
      const contentCenterX = (minX + maxX) / 2;
      const contentCenterY = (minY + maxY) / 2;
      viewport.scrollLeft = contentCenterX * scale - viewport.clientWidth / 2;
      viewport.scrollTop = contentCenterY * scale - viewport.clientHeight / 2;
    });
  }

  onWorkflowWheel(event: WheelEvent): void {
    if (!event.metaKey && !event.ctrlKey) {
      return;
    }

    event.preventDefault();
    this.zoomAnimated.set(false);
    if (this.zoomAnimationTimer != null) {
      clearTimeout(this.zoomAnimationTimer);
    }
    this.zoomAnimationTimer = setTimeout(() => {
      this.zoomAnimated.set(true);
      this.zoomAnimationTimer = null;
    }, 180);

    const factor = Math.exp(-event.deltaY * 0.0015);
    this.setWorkflowZoom(this.workflowZoom() * factor, event.clientX, event.clientY);
  }

  pickWorkflowAction(item: WorkflowActionCatalogItem, event?: Event): void {
    event?.stopPropagation();
    const target = this.actionPickerTarget() ?? { parentId: null, branch: null };
    const position = this.positionForNewAction(target);
    const action = createActionFromCatalog(item, position, target);
    this.workflowActions.update((actions) => [...actions, action]);
    this.selectedWorkflowActionId.set(action.id);
    this.closeActionPicker();
    this.repositionAllBubbles();
  }

  defaultNextPosition(target: WorkflowPickerTarget = { parentId: null, branch: null }): WorkflowPosition {
    return this.positionForNewAction(target);
  }

  repositionAddBubble(): void {
    const roots = rootActions(this.workflowActions());
    if (roots.length === 0) {
      this.addBubblePosition.set(
        this.bubblePositionBelow(this.workflowTriggerPosition(), WORKFLOW_ACTION_HEIGHT),
      );
      return;
    }

    const last = roots[roots.length - 1];
    this.addBubblePosition.set(
      this.bubblePositionBelow({ x: last.x, y: last.y }, this.workflowNodeHeight(last)),
    );
  }

  repositionAllBubbles(): void {
    this.repositionAddBubble();
    const actions = this.workflowActions();
    const next: Record<string, BranchBubblePair> = {};
    for (const ifElse of actions.filter((action) => action.type === 'if_else')) {
      next[ifElse.id] = {
        then: this.bubbleForBranchEnd(actions, ifElse, 'then'),
        else: this.bubbleForBranchEnd(actions, ifElse, 'else'),
      };
    }
    this.branchBubbles.set(next);
  }

  private positionForNewAction(target: WorkflowPickerTarget): WorkflowPosition {
    const actions = this.workflowActions();

    if (target.parentId && target.branch) {
      const parent = actions.find((action) => action.id === target.parentId);
      const kids = parent
        ? branchActions(actions, target.parentId, target.branch)
        : [];
      if (kids.length > 0) {
        const last = kids[kids.length - 1];
        return {
          x: last.x,
          y: last.y + this.workflowNodeHeight(last) + WORKFLOW_NODE_GAP,
        };
      }
      if (parent) {
        const offsetX =
          target.branch === 'then' ? -WORKFLOW_BRANCH_OFFSET_X : WORKFLOW_BRANCH_OFFSET_X;
        return {
          x: Math.max(0, parent.x + offsetX),
          y: parent.y + WORKFLOW_BRANCH_HEIGHT + WORKFLOW_NODE_GAP,
        };
      }
    }

    const roots = rootActions(actions);
    if (roots.length === 0) {
      const trigger = this.workflowTriggerPosition();
      return {
        x: trigger.x,
        y: trigger.y + WORKFLOW_ACTION_HEIGHT + WORKFLOW_NODE_GAP,
      };
    }

    const last = roots[roots.length - 1];
    return {
      x: last.x,
      y: last.y + this.workflowNodeHeight(last) + WORKFLOW_NODE_GAP,
    };
  }

  private bubbleForBranchEnd(
    actions: WorkflowAction[],
    ifElse: WorkflowAction,
    branch: WorkflowBranch,
  ): WorkflowPosition {
    const kids = branchActions(actions, ifElse.id, branch);
    if (kids.length > 0) {
      const last = kids[kids.length - 1];
      return this.bubblePositionBelow({ x: last.x, y: last.y }, this.workflowNodeHeight(last));
    }
    return this.defaultBranchBubbles(ifElse)[branch];
  }

  private defaultBranchBubbles(ifElse: WorkflowAction): BranchBubblePair {
    const baseY = ifElse.y + WORKFLOW_BRANCH_HEIGHT + WORKFLOW_NODE_GAP;
    return {
      then: {
        x: Math.max(
          0,
          ifElse.x - WORKFLOW_BRANCH_OFFSET_X + WORKFLOW_NODE_WIDTH / 2 - WORKFLOW_BUBBLE_SIZE / 2,
        ),
        y: baseY,
      },
      else: {
        x: Math.max(
          0,
          ifElse.x + WORKFLOW_BRANCH_OFFSET_X + WORKFLOW_NODE_WIDTH / 2 - WORKFLOW_BUBBLE_SIZE / 2,
        ),
        y: baseY,
      },
    };
  }

  branchBubbleId(ifElseId: string, branch: WorkflowBranch): string {
    return `branch-bubble:${ifElseId}:${branch}`;
  }

  private workflowNodeHeight(action: WorkflowAction): number {
    return action.type === 'if_else' ? WORKFLOW_BRANCH_HEIGHT : WORKFLOW_ACTION_HEIGHT;
  }

  private livePosition(id: string, fallback: WorkflowPosition): WorkflowPosition {
    const override = this.dragOverride();
    return override && override.id === id
      ? { x: override.x, y: override.y }
      : { x: fallback.x, y: fallback.y };
  }

  private bubblePositionBelow(node: WorkflowPosition, height: number): WorkflowPosition {
    return {
      x: node.x + WORKFLOW_NODE_WIDTH / 2 - WORKFLOW_BUBBLE_SIZE / 2,
      y: node.y + height + WORKFLOW_NODE_GAP,
    };
  }

  /** Unused while config lives in the right panel; kept for easy restore. */
  openWorkflowActionConfig(action: WorkflowAction): void {
    const dialogRef = this.overlayService.openModal(WorkflowActionConfigOverlayComponent, {
      data: { action },
      panelClass: ['workflow-action-config-overlay-panel'],
    });

    dialogRef.closed.subscribe((result: WorkflowActionConfigOverlayResult) => {
      if (!result) {
        return;
      }

      this.workflowActions.update((actions) =>
        actions.map((item) => (item.id === result.id ? result : item)),
      );
    });
  }

  toggleComponentsPanel(): void {
    this.componentsPanelOpen.update((open) => !open);
  }

  closeComponentsPanel(): void {
    this.componentsPanelOpen.set(false);
  }

  selectComponentForPrompt(item: FieldCatalogItem): void {
    if (!item.available) {
      return;
    }
    this.prompt.set(item.promptHint);
    this.componentsPanelOpen.set(false);
  }

  addFieldFromCatalog(item: FieldCatalogItem): void {
    const field = createFieldFromCatalog(item);
    if (!field) {
      return;
    }
    this.insertField(field);
  }

  onPaletteCatalogClick(item: FieldCatalogItem): void {
    if (this.paletteSuppressCatalogClick) {
      return;
    }
    this.addFieldFromCatalog(item);
  }

  onPaletteDragStart(item: FieldCatalogItem, event: PointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    const target = event.target;
    if (target instanceof Element && target.closest('button')) {
      return;
    }

    event.preventDefault();
    this.detachPalettePointerListeners();

    const startX = event.clientX;
    const startY = event.clientY;
    let dragStarted = false;
    let movedPastThreshold = false;

    const onSelectStart = (selectEvent: Event): void => {
      selectEvent.preventDefault();
    };

    const onMove = (moveEvent: PointerEvent): void => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      if (
        !dragStarted &&
        dx * dx + dy * dy <
          AiFormBuilderPage.PALETTE_DRAG_THRESHOLD_PX *
            AiFormBuilderPage.PALETTE_DRAG_THRESHOLD_PX
      ) {
        return;
      }

      if (!dragStarted) {
        dragStarted = true;
        movedPastThreshold = true;
        window.getSelection()?.removeAllRanges();
        this.paletteDragItem.set(item);
        this.previewDragging.set(true);
      }

      this.palettePointerX = moveEvent.clientX;
      this.palettePointerY = moveEvent.clientY;
      this.paletteGhostX.set(moveEvent.clientX);
      this.paletteGhostY.set(moveEvent.clientY);
      this.updatePaletteDropIndex(moveEvent.clientX, moveEvent.clientY);
      this.ensurePaletteAutoScrollLoop();
    };

    const onEnd = (): void => {
      this.detachPalettePointerListeners();
      this.stopPaletteAutoScrollLoop();
      if (dragStarted) {
        const dropIndex = this.paletteDropIndex();
        const dragItem = this.paletteDragItem();
        if (dropIndex !== null && dragItem) {
          const field = createFieldFromCatalog(dragItem);
          if (field) {
            this.insertFieldAt(dropIndex, field);
          }
        }
        if (movedPastThreshold) {
          this.paletteSuppressCatalogClick = true;
          queueMicrotask(() => {
            this.paletteSuppressCatalogClick = false;
          });
        }
      }
      this.paletteDragItem.set(null);
      this.paletteDropIndex.set(null);
      this.previewDragging.set(false);
    };

    document.addEventListener('selectstart', onSelectStart);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);

    this.palettePointerCleanup = (): void => {
      document.removeEventListener('selectstart', onSelectStart);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
    };
  }

  private detachPalettePointerListeners(): void {
    this.palettePointerCleanup?.();
    this.palettePointerCleanup = null;
    this.stopPaletteAutoScrollLoop();
  }

  private getFormPreviewScrollEl(): HTMLElement | null {
    return (
      this.formPreviewScroll()?.nativeElement ??
      document.querySelector<HTMLElement>('[data-form-preview-scroll]')
    );
  }

  private autoScrollFormPreview(clientY: number): void {
    const scrollEl = this.getFormPreviewScrollEl();
    if (!scrollEl) {
      return;
    }

    const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight;
    if (maxScroll <= 0) {
      return;
    }

    const rect = scrollEl.getBoundingClientRect();
    const edge = AiFormBuilderPage.PALETTE_AUTO_SCROLL_EDGE_PX;
    const maxSpeed = AiFormBuilderPage.PALETTE_AUTO_SCROLL_MAX_SPEED_PX;
    let delta = 0;

    if (clientY < rect.top + edge) {
      const depth = Math.min(1, (rect.top + edge - clientY) / edge);
      delta = -Math.ceil(maxSpeed * depth);
    } else if (clientY > rect.bottom - edge) {
      const depth = Math.min(1, (clientY - (rect.bottom - edge)) / edge);
      delta = Math.ceil(maxSpeed * depth);
    }

    if (delta === 0) {
      return;
    }

    const nextTop = Math.max(0, Math.min(maxScroll, scrollEl.scrollTop + delta));
    if (nextTop !== scrollEl.scrollTop) {
      scrollEl.scrollTop = nextTop;
    }
  }

  private ensurePaletteAutoScrollLoop(): void {
    if (this.paletteAutoScrollRafId != null || this.paletteDragItem() === null) {
      return;
    }

    const tick = (): void => {
      if (this.paletteDragItem() === null) {
        this.paletteAutoScrollRafId = null;
        return;
      }
      this.autoScrollFormPreview(this.palettePointerY);
      this.updatePaletteDropIndex(this.palettePointerX, this.palettePointerY);
      this.paletteAutoScrollRafId = requestAnimationFrame(tick);
    };

    this.paletteAutoScrollRafId = requestAnimationFrame(tick);
  }

  private stopPaletteAutoScrollLoop(): void {
    if (this.paletteAutoScrollRafId != null) {
      cancelAnimationFrame(this.paletteAutoScrollRafId);
      this.paletteAutoScrollRafId = null;
    }
  }

  private updatePaletteDropIndex(clientX: number, clientY: number): void {
    const dropList = document.getElementById('preview-fields');
    if (!dropList) {
      this.paletteDropIndex.set(null);
      return;
    }

    const rect = dropList.getBoundingClientRect();
    const inside =
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom;
    if (!inside) {
      this.paletteDropIndex.set(null);
      return;
    }

    const rows = dropList.querySelectorAll<HTMLElement>('.preview-field-row');
    if (rows.length === 0) {
      this.paletteDropIndex.set(0);
      return;
    }

    let index = rows.length;
    for (let i = 0; i < rows.length; i++) {
      const rowRect = rows[i].getBoundingClientRect();
      const midY = rowRect.top + rowRect.height / 2;
      if (clientY < midY) {
        index = i;
        break;
      }
    }
    this.paletteDropIndex.set(index);
  }

  onPreviewDrop(event: CdkDragDrop<FormField[]>): void {
    if (event.previousContainer !== event.container) {
      return;
    }
    const pageId = this.activePageId();
    this.builtForm.update((form) => {
      if (!form) {
        return form;
      }
      const page = findPage(form, pageId);
      if (!page) {
        return form;
      }
      const fields = [...page.fields];
      moveItemInArray(fields, event.previousIndex, event.currentIndex);
      return updatePageFields(form, page.id, fields);
    });
    this.scheduleAutosave();
  }

  selectField(field: FormField, event?: Event): void {
    event?.stopPropagation();
    this.closeFormSettings();
    this.selectedFieldId.set(field.id);
    this.configTab.set('field');
  }

  setConfigTab(tab: 'page' | 'field'): void {
    this.closeFormSettings();
    this.configTab.set(tab);
    if (tab === 'page') {
      this.selectedFieldId.set(null);
    }
  }

  clearFieldSelection(): void {
    this.selectedFieldId.set(null);
  }

  setActivePageId(pageId: string): void {
    this.activePageId.set(pageId);
    this.selectedFieldId.set(null);
    this.configTab.set('page');
  }

  openFormSettings(event?: Event): void {
    event?.stopPropagation();
    this.setBuilderStep('form');
    this.formSettingsOpen.set(true);
  }

  closeFormSettings(): void {
    this.formSettingsOpen.set(false);
  }

  openPagesManager(): void {
    const dialogRef = this.overlayService.openModal(FormPagesOverlayComponent, {
      data: {
        pages: this.orderedPages(),
        activePageId: this.activePageId(),
      },
      fitContent: true,
    });

    dialogRef.closed.subscribe(
      (result: FormPagesOverlayResult | null | undefined) => {
        if (!result) {
          return;
        }
        const pages = applyPageSequence(result.pages);
        this.builtForm.update((form) =>
          form ? { ...form, pages } : form,
        );
        this.setActivePageId(result.activePageId);
        this.scheduleAutosave();
      },
    );
  }

  addFormPage(): void {
    this.builtForm.update((form) => {
      const base = form ?? createEmptyBuiltForm();
      const pages = sortedPages(base.pages);
      if (pages.length > 0) {
        const last = pages[pages.length - 1];
        if (
          last.cta.primaryLabel.trim().toLowerCase() ===
          DEFAULT_PRIMARY_LABEL.toLowerCase()
        ) {
          pages[pages.length - 1] = {
            ...last,
            cta: { ...last.cta, primaryLabel: DEFAULT_NEXT_LABEL },
          };
        }
      }
      const newPage = createDefaultPage({
        id: nextPageId(pages),
        order: pages.length + 1,
        cta: {
          primaryLabel: DEFAULT_PRIMARY_LABEL,
          backLabel: 'Back',
        },
      });
      const nextPages = reindexPageOrders([...pages, newPage]);
      queueMicrotask(() => this.setActivePageId(newPage.id));
      return { ...base, pages: nextPages };
    });
    this.scheduleAutosave();
  }

  deleteActivePage(): void {
    const pageId = this.activePageId();
    this.builtForm.update((form) => {
      if (!form || form.pages.length <= 1) {
        return form;
      }
      const remaining = reindexPageOrders(
        form.pages.filter((page) => page.id !== pageId),
      );
      const nextActive = remaining[remaining.length - 1] ?? remaining[0];
      queueMicrotask(() => {
        if (nextActive) {
          this.setActivePageId(nextActive.id);
        }
      });
      return { ...form, pages: remaining };
    });
    this.selectedFieldId.set(null);
    this.scheduleAutosave();
  }

  onPageStepConfigChange(updated: FormPage): void {
    this.builtForm.update((form) => {
      if (!form) {
        return form;
      }
      return {
        ...form,
        pages: form.pages.map((page) =>
          page.id === updated.id ? updated : page,
        ),
      };
    });
    this.scheduleAutosave();
  }

  private flushAutosave(): void {
    if (this.autosaveTimer == null) {
      return;
    }
    clearTimeout(this.autosaveTimer);
    this.autosaveTimer = null;
    this.persistFormDefinition();
  }

  private scheduleAutosave(): void {
    if (this.loadInProgress) {
      return;
    }
    const slug = this.formSlug().trim();
    const form = this.builtForm();
    if (!slug || !form) {
      return;
    }

    if (this.autosaveTimer != null) {
      clearTimeout(this.autosaveTimer);
    }

    this.autosaveTimer = setTimeout(() => {
      this.autosaveTimer = null;
      this.persistFormDefinition();
    }, 500);
  }

  private persistFormDefinition(): void {

    const form = this.builtForm();
    const slug = this.formSlug().trim();
    // const form = this.builtForm();
    if (!slug || !form || this.loadInProgress) {
      return;
    }

    const sequence = ++this.saveSequence;
    this.saveStatus.set('saving');

    this.executeRequest({
      state: this.saveFormState,
      request: this.userAPIService.saveFormDefinition(slug, mapBuiltFormToDto(form)),
      handleError: false,
      onSuccess: () => {
        if (sequence !== this.saveSequence) {
          return;
        }
        this.saveStatus.set('saved');
      },
      onFailed: () => {
        if (sequence !== this.saveSequence) {
          return;
        }
        this.saveStatus.set('error');
      },
    });
  }

  onFieldConfigChange(updated: FormField): void {
    const pageId = this.activePageId();
    this.builtForm.update((form) => {
      if (!form) {
        return form;
      }
      const page = findPage(form, pageId);
      if (!page) {
        return form;
      }
      return updatePageFields(
        form,
        page.id,
        page.fields.map((item) => (item.id === updated.id ? updated : item)),
      );
    });
    this.scheduleAutosave();
  }

  removeSelectedField(): void {
    const id = this.selectedFieldId();
    const pageId = this.activePageId();
    if (!id) {
      return;
    }
    this.builtForm.update((form) => {
      if (!form) {
        return form;
      }
      const page = findPage(form, pageId);
      if (!page) {
        return form;
      }
      return updatePageFields(
        form,
        page.id,
        page.fields.filter((f) => f.id !== id),
      );
    });
    this.selectedFieldId.set(null);
    this.scheduleAutosave();
  }

  duplicateSelectedField(): void {
    const id = this.selectedFieldId();
    const page = this.activePage();
    if (!id || !page) {
      return;
    }
    const index = page.fields.findIndex((field) => field.id === id);
    if (index < 0) {
      return;
    }
    const clone = cloneFieldWithNewId(page.fields[index]);
    this.insertFieldAt(index + 1, clone);
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Delete' || !this.selectedFieldId()) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    if (
      target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]')
    ) {
      return;
    }
    event.preventDefault();
    this.removeSelectedField();
  }

  onPageConfigChange(updated: BuiltForm): void {
    this.builtForm.set(updated);

    this.scheduleAutosave();
  }

  useSuggestion(text: string): void {
    this.prompt.set(text);
    this.generate();
  }

  onPromptKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.generate();
    }
  }

  onCanvasPromptKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.submitCanvasPrompt();
    }
  }

  submitCanvasPrompt(): void {
    const text = this.canvasPrompt().trim();
    if (!text || this.isGenerating()) {
      return;
    }
    this.setBuilderMode('ai');
    this.prompt.set(text);
    this.canvasPrompt.set('');
    this.generate();
  }

  generate(): void {
    const text = this.prompt().trim();
    if (!text || this.isGenerating()) {
      return;
    }

    const current = this.builtForm() ?? createEmptyBuiltForm();
    const activePage =
      findPage(current, this.activePageId()) ??
      createDefaultPage({ fields: [] });
    const hasAnyFields = allFormFields(current).length > 0;
    const mode = hasAnyFields
      ? AiGenerateRequestMode.update
      : AiGenerateRequestMode.create;

    const pageContext =
      current.pages.length > 1
        ? `Editing page ${activePage.order}${
            activePage.title ? ` (“${activePage.title}”)` : ''
          } of a multi-page form. Only change this page's fields.\n\n`
        : '';

    const payload: AiGenerateRequest = {
      prompt: `${pageContext}${text}`,
      mode,
      catalog: toAiCatalogPayload(FIELD_CATALOG.filter((item) => item.available)),
      pageSettings: {
        title: current.title,
        description: current.description,
        submitLabel: activePage.cta.primaryLabel,
        coverImageUrl: current.coverImageUrl,
        inputAppearance: current.inputAppearance ?? 'fill',
      },
      currentForm:
        mode === AiGenerateRequestMode.update
          ? mapActivePageToAiDto(current, activePage)
          : undefined,
    };

    this.messages.update((msgs) => [...msgs, { role: 'user', content: text }]);
    this.prompt.set('');
    this.isGenerating.set(true);
    this.scrollChatToBottom();

    this.executeRequest({
      state: this.generateState,
      request: this.userAPIService.generateFormWithAi(payload),
      handleError: true,
      onSuccess: (response) => {
        const aiForm = mapBuiltFormDtoToBuiltForm(response.data.form);
        const aiPage = sortedPages(aiForm.pages)[0];
        const targetPageId = this.activePageId();

        if (mode === AiGenerateRequestMode.create) {
          const page = createDefaultPage({
            id: DEFAULT_PAGE_ID,
            order: 1,
            cta: {
              primaryLabel:
                aiPage?.cta.primaryLabel?.trim() || DEFAULT_PRIMARY_LABEL,
            },
            fields: aiPage?.fields ?? [],
          });
          this.builtForm.set({
            title: aiForm.title,
            ...(aiForm.description ? { description: aiForm.description } : {}),
            ...(aiForm.coverImageUrl
              ? { coverImageUrl: aiForm.coverImageUrl }
              : {}),
            inputAppearance: aiForm.inputAppearance,
            themeColor: aiForm.themeColor,
            pages: [page],
          });

          this.activePageId.set(DEFAULT_PAGE_ID);
        } else {
          this.builtForm.update((form) => {
            const base = form ?? createEmptyBuiltForm();
            return {
              ...base,
              pages: base.pages.map((page) => {
                if (page.id !== targetPageId) {
                  return page;
                }
                return {
                  ...page,
                  fields: aiPage?.fields ?? [],
                  cta: {
                    ...page.cta,
                    primaryLabel:
                      aiPage?.cta.primaryLabel?.trim() ||
                      page.cta.primaryLabel ||
                      DEFAULT_PRIMARY_LABEL,
                  },
                };
              }),
            };
          });
        }



        this.selectedFieldId.set(null);
        this.configTab.set('page');
        this.messages.update((msgs) => [
          ...msgs,
          { role: 'assistant', content: response.data.assistantMessage },
        ]);
        this.isGenerating.set(false);
        this.scrollChatToBottom();
        this.scheduleAutosave();
      },
      onFailed: (error) => {
        const errorMessages = error?.error?.errorMessages ?? [];
        const message =
          errorMessages[0] ??
          'Sorry — form generation failed. Please try again.';
        this.messages.update((msgs) => [
          ...msgs,
          { role: 'assistant', content: message },
        ]);
        this.isGenerating.set(false);
        this.scrollChatToBottom();
      },
    });
  }

  private insertField(field: FormField): void {
    const pageId = this.activePageId();
    this.builtForm.update((form) => {
      const base = form ?? createEmptyBuiltForm();
      const page = findPage(base, pageId) ?? sortedPages(base.pages)[0];
      if (!page) {
        return {
          ...base,
          pages: [createDefaultPage({ fields: [field] })],
        };
      }
      return updatePageFields(base, page.id, [...page.fields, field]);
    });
    this.closeFormSettings();
    this.selectedFieldId.set(field.id);
    this.configTab.set('field');
    this.scheduleAutosave();
    this.scrollPreviewFieldIntoView(field.id, 'end');
  }

  private insertFieldAt(index: number, field: FormField): void {
    const pageId = this.activePageId();
    this.builtForm.update((form) => {
      const base = form ?? createEmptyBuiltForm();
      const page = findPage(base, pageId) ?? sortedPages(base.pages)[0];
      if (!page) {
        return {
          ...base,
          pages: [createDefaultPage({ fields: [field] })],
        };
      }
      const fields = [...page.fields];
      const insertAt = Math.max(0, Math.min(index, fields.length));
      fields.splice(insertAt, 0, field);
      return updatePageFields(base, page.id, fields);
    });
    this.closeFormSettings();
    this.selectedFieldId.set(field.id);
    this.configTab.set('field');
    this.scheduleAutosave();
    const align =
      this.activePageFields().at(-1)?.id === field.id ? 'end' : 'nearest';
    this.scrollPreviewFieldIntoView(field.id, align);
  }

  private scrollPreviewFieldIntoView(
    fieldId: string,
    align: 'nearest' | 'end' = 'nearest',
  ): void {
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        if (align === 'end') {
          const scrollEl = this.getFormPreviewScrollEl();
          if (scrollEl) {
            scrollEl.scrollTo({ top: scrollEl.scrollHeight, behavior: 'smooth' });
          }
          return;
        }
        const row = document.querySelector<HTMLElement>(
          `[data-preview-field-id="${CSS.escape(fieldId)}"]`,
        );
        row?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      });
    });
  }

  private scrollChatToBottom(): void {
    queueMicrotask(() => {
      const el = this.chatScroll()?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
